import { Search, type LucideIcon } from 'lucide-react';
import { cn } from 'lib/utils';

/**
 * The mock-ups' empty state: a rounded icon tile, a sentence, and a hint on what to do next.
 *
 * A table that comes back empty and says only "no result" leaves the reader unable to tell a
 * filter that is too narrow from a desk that genuinely has nothing. The hint is the whole point;
 * the icon is what stops the block reading as an error.
 *
 * Extracted from `AdminTechnicians` when the ticket screen needed the same block — the second
 * caller, not a speculative one.
 */
const TONES = {
  neutral: 'bg-zinc-100 text-muted-foreground/70 dark:bg-zinc-800',
  success: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400',
  danger: 'bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-400',
};

interface EmptyStateProps {
  title: string;
  hint?: string;
  /** Defaults to the magnifier: most empty states here are a filter that matched nothing. */
  icon?: LucideIcon;
  tone?: keyof typeof TONES;
}

export const EmptyState = ({ title, hint, icon: Icon = Search, tone = 'neutral' }: EmptyStateProps) => (
  <div className="flex flex-col items-center py-[54px] text-center">
    <span className={cn('mb-[14px] flex h-[46px] w-[46px] items-center justify-center rounded-xl', TONES[tone])}>
      <Icon className="h-[22px] w-[22px]" aria-hidden="true" />
    </span>
    <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{title}</p>
    {hint && <p className="mt-[5px] text-[13px] text-muted-foreground">{hint}</p>}
  </div>
);

export default EmptyState;
